import { useCallback, useEffect, useMemo, useState } from "react";
import { EnvironmentOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useTranslation } from "react-i18next";
import useSessionState from "../../../hooks/useSessionState";

import apiClient from "../../../helpers/apiClient";
import { useCategoriesContext } from "../../../contexts/CategoriesContext";
import { useToast } from "../../../contexts/ToastContext";
import CustomPagination from "../../../components/custom-component/CustomPagination";
import { MobileFilterBar, useTableFilters } from "../../../components/data-table";
import LocationFormModal from "../LocationFormModal";
import LocationList from "./LocationList";

function LocationListPanel() {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const { removeLocation, updateLocationCount } = useCategoriesContext();

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useSessionState("locations:page", 1);
    const [pageSize, setPageSize] = useSessionState("locations:pageSize", 10);
    const [totalItems, setTotalItems] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const table = useTableFilters({ persistKey: "locations", onChange: () => setCurrentPage(1) });
    const searchParams = table.filters;

    const loadLocations = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                limit: pageSize.toString(),
                page: currentPage.toString(),
                ...searchParams,
            });

            const response = await apiClient.get(`/api/location/list?${queryParams}`);
            if (response?.data?.success) {
                const total = response?.data?.meta?.total || 0;
                setTotalItems(total);
                setLocations(response?.data?.data || []);
                updateLocationCount(total);
            } else {
                setLocations([]);
                setTotalItems(0);
            }
        } catch (_error) {
            setLocations([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchParams, updateLocationCount]);

    useEffect(() => {
        loadLocations();
    }, [loadLocations]);

    const locationActionItems = useMemo(
        () => [
            { label: t("edit"), key: "edit" },
            { label: t("delete"), key: "delete", danger: true },
        ],
        [t],
    );

    const openCreateModal = () => {
        setEditingLocation(null);
        setModalOpen(true);
    };

    const openEditModal = (location) => {
        setEditingLocation(location);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingLocation(null);
    };

    const deleteLocation = async (locationId) => {
        setActionLoadingId(locationId);
        try {
            const response = await apiClient.delete(`/api/location/${locationId}`);
            if (response?.data?.success) {
                removeLocation(locationId);
                toast.success({
                    message: t("locations.successfullyDeletedLocation"),
                });
                await loadLocations();
            }
        } catch (_error) {
            toast.error({
                message: t("locations.failedToDelete"),
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDelete = (locationId) => {
        Modal.confirm({
            title: t("locations.confirmDelete"),
            okText: t("delete"),
            cancelText: t("rates.cancel"),
            centered: true,
            okButtonProps: { danger: true },
            onOk: () => deleteLocation(locationId),
        });
    };

    const handleActionClick = (key, location) => {
        if (key === "edit") {
            openEditModal(location);
            return;
        }
        if (key === "delete") {
            handleDelete(location.locationId);
        }
    };

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <MobileFilterBar
                filters={table}
                search={{ key: "searchTerm", placeholder: t("mobileList.searchLocationsPlaceholder") }}
                filterLabels={{
                    address: t("locations.address"),
                    city: t("locations.city"),
                    state: t("locations.state"),
                    country: t("locations.country"),
                }}
            />

            <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={openCreateModal}
                className="!h-11 w-full !rounded-xl !font-medium">
                {t("locations.createLocation")}
            </Button>

            <LocationList
                locations={locations}
                loading={loading}
                t={t}
                actionItems={locationActionItems}
                onActionClick={handleActionClick}
                actionLoadingId={actionLoadingId}
            />

            {totalItems > 0 ? (
                <div className="flex min-w-0 w-full justify-end">
                    <CustomPagination
                        variant="spaced"
                        totalItem={totalItems}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSizeOptions={[10, 20, 50, 100]}
                        showSizeChanger
                    />
                </div>
            ) : null}

            <LocationFormModal
                open={modalOpen}
                location={editingLocation}
                onCancel={closeModal}
                onSuccess={() => {
                    closeModal();
                    loadLocations();
                }}
            />
        </div>
    );
}

export default LocationListPanel;
